import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import avatarFallback from "@/assets/UI/avatar.png";
import coinIcon from "@/assets/UI/coin.webp";
import walletIcon from "@/assets/UI/wallet.webp";
import paymentOptionsIcon from "@/assets/UI/paymentOptions.webp";

import { formatDateTime } from "@/utils/formatDateTime";
import s from "./BankTransactionDetail.module.scss";

const getUserId = (u) => u?.id || u?._id || u?.userId || null;

const BankTransactionDetail = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();

  const [entry, setEntry] = useState(null);
  const [account, setAccount] = useState(null);
  const [user, setUser] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [walletId, setWalletId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchEntry = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          `/v1/admin/finance/accounts/bank/transactions/${entryId}`,
        );

        if (!isMounted) return;

        setEntry(res.data?.entry ?? null);
        setAccount(res.data?.account ?? null);
        setUser(res.data?.user ?? null);
        setAvatar(res.data?.avatar ?? null);
        setWalletId(res.data?.walletId ?? null);
      } catch (err) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load bank transaction",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchEntry();

    return () => {
      isMounted = false;
    };
  }, [entryId]);

  const isDebit = entry?.entryType === "debit";
  const userId = useMemo(() => getUserId(user), [user]);

  const memberPath = useMemo(() => {
    return userId ? `/admin/members/single/${userId}` : null;
  }, [userId]);

  const walletPath = useMemo(() => {
    return walletId ? `/admin/finance/wallets/${walletId}` : null;
  }, [walletId]);

  // Prefer explicit id if backend provides it; otherwise fall back to sourceId
  const memberPaymentSubmissionId =
    entry?.memberPaymentSubmissionId || entry?.sourceId || null;

  const paymentInfoPath = useMemo(() => {
    if (!userId || !memberPaymentSubmissionId) return null;
    return `/admin/members/single/payment-info/single/${userId}/${memberPaymentSubmissionId}`;
  }, [userId, memberPaymentSubmissionId]);

  const renderPrettyDateTime = (value) => {
    const dt = formatDateTime(value);
    if (!dt) return <span className={s.dash}>-</span>;

    const hasParts =
      dt.day !== undefined && dt.suffix && dt.month && dt.year !== undefined;

    return (
      <div className={s.dateTime}>
        <div className={s.date}>
          {hasParts ? (
            <>
              {dt.day}
              <sup className={s.sup}>{dt.suffix}</sup> {dt.month} {dt.year}
            </>
          ) : (
            dt.date
          )}
        </div>
        <div className={s.time}>{dt.time}</div>
      </div>
    );
  };

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Bank</p>
          <h1 className={s.title}>Bank Transaction Detail</h1>
          <p className={s.subtitle}>
            Review the member, balance impact, and accounting references.
          </p>
        </div>

        <div className={s.actions}>
          <button className={s.btn} onClick={() => navigate(-1)}>
            Back
          </button>

          <Link
            className={`${s.btn} ${s.btnLedger}`}
            to="/admin/finance/accounts/bank"
          >
            <span className={s.emoji}>🏦</span>
            <span>Bank Ledger</span>
          </Link>

          {walletPath ? (
            <Link className={`${s.btn} ${s.btnWallet}`} to={walletPath}>
              <img className={s.btnIcon} src={walletIcon} alt="" />
              <span>Wallet</span>
            </Link>
          ) : (
            <span
              className={`${s.btn} ${s.btnDisabled}`}
              title="Wallet not available"
            >
              <img className={s.btnIcon} src={walletIcon} alt="" />
              <span>Wallet</span>
            </span>
          )}
        </div>
      </header>

      {loading ? (
        <div className={s.stateBox}>Loading bank transaction…</div>
      ) : error ? (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      ) : !entry ? (
        <div className={s.stateBox}>Transaction not found.</div>
      ) : (
        <div className={s.grid}>
          {/* Member */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Member</h2>
              {memberPath ? (
                <Link className={s.microLink} to={memberPath}>
                  Open profile →
                </Link>
              ) : (
                <span className={s.hint}>No profile</span>
              )}
            </div>

            {memberPath ? (
              <Link to={memberPath} className={s.memberRow}>
                <span className={s.avatarRing}>
                  <img
                    src={avatar?.url || avatarFallback}
                    alt=""
                    className={s.avatarLg}
                  />
                </span>

                <span className={s.memberMeta}>
                  <span className={s.memberName}>
                    {user?.username || "Unknown user"}
                  </span>
                  <span className={s.memberSub}>
                    Tap to view member profile
                  </span>
                </span>
              </Link>
            ) : (
              <div className={s.memberRowStatic}>
                <span className={s.avatarRing}>
                  <img
                    src={avatar?.url || avatarFallback}
                    alt=""
                    className={s.avatarLg}
                  />
                </span>

                <div className={s.memberMeta}>
                  <div className={s.memberName}>
                    {user?.username || "Unknown user"}
                  </div>
                  <div className={s.memberSub}>Member not available</div>
                </div>
              </div>
            )}

            <div className={s.divider} />

            <div className={s.balanceBox}>
              <div className={s.balanceTop}>
                <div>
                  <p className={s.muted}>Current bank balance</p>
                  <p className={s.balanceName}>{account?.name || "Bank"}</p>
                </div>

                <span
                  className={`${s.typePill} ${isDebit ? s.typeDebit : s.typeCredit}`}
                >
                  {entry.entryType || "-"}
                </span>
              </div>

              <div className={s.balanceMetrics}>
                <div className={s.metricCard}>
                  <p className={s.muted}>NOTY</p>
                  <div className={s.metricLine}>
                    <span className={s.metricValue}>
                      {account?.balanceNoty ?? 0}
                    </span>
                    <img className={s.coinMd} src={coinIcon} alt="" />
                  </div>
                </div>

                <div className={s.metricCard}>
                  <p className={s.muted}>CAD</p>
                  <div className={s.metricLine}>
                    <span className={s.metricValue}>
                      {account?.balanceCad ?? 0}
                    </span>
                    <span className={s.metricUnit}>CAD</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Transaction */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Transaction</h2>
              <span className={s.hint}>Reason + amount</span>
            </div>

            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Reason</p>
                <p className={s.bigText}>{entry.reason || "-"}</p>
              </div>
              <div className={s.k}>
                <p className={s.muted}>Amount</p>
                <div
                  className={`${s.amountLine} ${
                    isDebit ? s.amountDebit : s.amountCredit
                  }`}
                >
                  <span className={s.sign}>{isDebit ? "+" : "-"}</span>
                  <span className={s.amountValue}>{entry.amountNoty ?? 0}</span>
                  <img className={s.coinSm} src={coinIcon} alt="" />
                </div>
              </div>
            </div>
            <div className={s.dividerSoft} />
            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Exchange rate</p>
                <p className={s.valueText}>{entry.exchangeRate ?? 0}</p>
              </div>
              <div className={s.k}>
                <p className={s.muted}>Amount (CAD)</p>
                <p className={s.valueText}>{entry.amountCad ?? 0}</p>
              </div>
            </div>

            <div className={s.dividerSoft} />

            <div className={s.k}>
              <p className={s.muted}>Balance after</p>
              <div className={s.balanceAfterLine}>
                <span className={s.balanceAfterValue}>
                  {entry.balanceAfterNoty ?? "-"}
                </span>
                <img className={s.coinSm} src={coinIcon} alt="" />
              </div>
              <p className={s.helperText}>CAD {entry.balanceAfterCad ?? 0}</p>
            </div>
          </section>

          {/* Timing */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Timing</h2>
              <span className={s.hint}>Created + updated</span>
            </div>

            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Created</p>
                {renderPrettyDateTime(entry.createdAt)}
              </div>

              <div className={s.k}>
                <p className={s.muted}>Last updated</p>
                {renderPrettyDateTime(entry.updatedAt)}
              </div>
            </div>
          </section>

          {/* References */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>References</h2>

              {paymentInfoPath ? (
                <Link
                  className={s.refBtn}
                  to={paymentInfoPath}
                  title="Open payment info"
                >
                  <img className={s.refIcon} src={paymentOptionsIcon} alt="" />
                  <span>Payment Info</span>
                </Link>
              ) : (
                <span className={s.hint}>No payment info</span>
              )}
            </div>

            <div className={s.kv}>
              <div className={s.k}>
                <p className={s.muted}>Source type</p>
                <p className={s.valueText}>{entry.sourceType || "-"}</p>
              </div>

              <div className={s.k}>
                <p className={s.muted}>IP address</p>
                <p className={s.valueText}>{entry.ipAddress || "-"}</p>
              </div>

              <div className={s.k}>
                <p className={s.muted}>User agent</p>
                <p className={s.valueTextClamp}>{entry.userAgent || "-"}</p>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default BankTransactionDetail;
